import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Archive, RotateCcw, Search, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-ZA", {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

const R = (v) => (v == null ? "—" : `R ${Number(v).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export default function ArchivedProductsModal({ open, onOpenChange, onRestored }) {
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [restoring, setRestoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/products/admin/archived`, {
        params: { search: search || undefined, limit: 1000 },
      });
      setRows(r.data.products || []);
      setTotal(r.data.total || 0);
      setSelected(new Set());
    } catch (e) {
      toast.error("Failed to load archived products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open]);

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const restore = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Restore ${selected.size} archived product${selected.size === 1 ? "" : "s"}? They will be visible in Stock Master again.`)) return;
    setRestoring(true);
    try {
      const r = await axios.post(`${API}/products/admin/bulk-unarchive`, {
        product_ids: Array.from(selected),
      });
      toast.success(`Restored ${r.data.restored} product${r.data.restored === 1 ? "" : "s"}`);
      setSelected(new Set());
      await load();
      onRestored && onRestored();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="archived-products-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-amber-600" />
            Archived Products
          </DialogTitle>
          <DialogDescription>
            Products that were soft-deleted. They stay hidden from Stock Master search but remain in the database.
            Select any items that should not be archived and click <strong>Restore Selected</strong> to bring them back.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name, PLU or supplier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-9"
              data-testid="archived-search-input"
            />
          </div>
          <Button variant="outline" onClick={load} disabled={loading} data-testid="archived-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={restore}
            disabled={selected.size === 0 || restoring}
            className="bg-green-600 hover:bg-green-700"
            data-testid="archived-restore-btn"
          >
            <RotateCcw className={`h-4 w-4 mr-1 ${restoring ? "animate-spin" : ""}`} />
            Restore Selected ({selected.size})
          </Button>
        </div>

        <div className="text-xs text-slate-500 mb-1 flex items-center gap-2">
          <Badge variant="secondary">{total} archived total</Badge>
          {rows.length < total && <span>Showing first {rows.length} — refine your search to see more.</span>}
        </div>

        <div className="overflow-auto border rounded-md bg-white flex-1">
          <table className="w-full text-sm" data-testid="archived-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase sticky top-0">
              <tr>
                <th className="p-2 text-left w-8">
                  <Checkbox
                    checked={rows.length > 0 && selected.size === rows.length}
                    onCheckedChange={toggleAll}
                    data-testid="archived-select-all"
                  />
                </th>
                <th className="p-2 text-left">PLU</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Supplier</th>
                <th className="p-2 text-left">Category</th>
                <th className="p-2 text-right">Stock</th>
                <th className="p-2 text-right">Price</th>
                <th className="p-2 text-left">Archived At</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Archive className="h-8 w-8 text-slate-300" />
                      <p>No archived products{search ? ` matching "${search}"` : ""}.</p>
                    </div>
                  </td>
                </tr>
              )}
              {!loading && rows.map((p) => (
                <tr
                  key={p.id}
                  className={`border-t hover:bg-slate-50 ${selected.has(p.id) ? "bg-amber-50" : ""}`}
                  data-testid={`archived-row-${p.plu_code || p.id}`}
                >
                  <td className="p-2">
                    <Checkbox
                      checked={selected.has(p.id)}
                      onCheckedChange={() => toggle(p.id)}
                      data-testid={`archived-check-${p.plu_code || p.id}`}
                    />
                  </td>
                  <td className="p-2 font-mono text-xs">{p.plu_code || "—"}</td>
                  <td className="p-2 font-medium">{p.name}</td>
                  <td className="p-2 text-slate-600">{p.supplier || "—"}</td>
                  <td className="p-2 text-slate-600">{p.category || "—"}</td>
                  <td className="p-2 text-right">{p.current_stock ?? 0}</td>
                  <td className="p-2 text-right">{R(p.selling_price)}</td>
                  <td className="p-2 text-xs text-slate-500">{fmtDate(p.archived_at || p.last_updated)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-900 flex items-start gap-2" data-testid="archived-note">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Archived products don&apos;t appear in Stock Master name-search, GRV picker, or till-side lookup.
              PLU/barcode lookups still find them (so old receipts keep working).
              Restoring makes them fully visible again.
            </span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
